/**
 * Réponse chiffrée à « Quel est le prix d'un chauffeur privé à <ville> ? ».
 *
 * MESURE 2026-10-09 : les pages ville répondaient « devis immédiat » sans un
 * seul prix, et la page Antibes citait un forfait Antibes ⇄ aéroport qui
 * n'existe pas dans la grille. Ici tout est lu dans les registres : les
 * trajets à prix fixe qui touchent la ville (data/transfers), la mise à
 * disposition (EVENT_PRICES) et, quand aucun forfait n'existe, le tarif au
 * kilomètre de la Classe E (infos.ts).
 */
import { VEHICLES } from "@/config/infos";
import {
  TRANSFER_ROUTES,
  getRouteMinPrice,
  getRoutePlaces,
} from "@/data/transfers";
import { placeShortName } from "@/data/transfers/places";
import { EVENT_PRICES as P } from "./eventPricing";

type Locale = "fr" | "en" | "ar";

/** Trajets à prix fixe qui partent de la ville ou y arrivent. */
const routesFor = (placeId: string, locale: Locale) =>
  TRANSFER_ROUTES.filter((r) => r.fromId === placeId || r.toId === placeId)
    .map((r) => {
      const { from, to } = getRoutePlaces(r);
      const other = r.fromId === placeId ? to : from;
      const name = placeShortName(other, locale);
      // « Aéroport de Nice » arrive au milieu d'une phrase française.
      const inSentence =
        locale === "fr" && name.startsWith("Aéroport")
          ? `a${name.slice(1)}`
          : name;
      return { other: inSentence, min: getRouteMinPrice(r) };
    })
    .filter((r): r is { other: string; min: number } => r.min !== null)
    .sort((a, b) => a.min - b.min);

/**
 * Titre et description chiffrés d'une ville qui a des trajets à prix fixe.
 * Échoue au build si la ville n'en a aucun : on n'affiche jamais « dès … ».
 */
export const citySeo = (
  locale: "fr" | "en",
  city: string,
  placeId: string,
  titleTail: string,
): { title: string; description: string } => {
  const routes = routesFor(placeId, locale);
  if (routes.length < 2)
    throw new Error(`cityPricing: pas assez de forfaits pour ${placeId}`);
  const [a, b] = routes;
  return locale === "en"
    ? {
        title: `Private chauffeur ${city}${titleTail} from €${a.min} | KS Rent Services`,
        description: `Private chauffeur in ${city}: ${a.other} from €${a.min}, ${b.other} from €${b.min}, hourly hire from €${P.e3} for 3 hours. Fixed prices, 24/7 booking.`,
      }
    : {
        title: `Chauffeur privé à ${city}${titleTail} dès ${a.min} €`,
        description: `Chauffeur privé et VTC à ${city} : ${a.other} dès ${a.min} €, ${b.other} dès ${b.min} €, mise à disposition dès ${P.e3} € les 3 h. Prix fixes, 24 h/24.`,
      };
};

const KM_E = VEHICLES.CLASS_E.pricePerKm;

const km = (locale: Locale) =>
  locale === "fr" ? KM_E.toFixed(2).replace(".", ",") : KM_E.toFixed(2);

/**
 * `placeId` absent = ville sans forfait (estimation au kilomètre).
 * `at` : la préposition française quand « à <ville> » est faux (« au Cannet »).
 */
export const cityPriceFaq = (
  locale: Locale,
  city: string,
  placeId?: string,
  at = `à ${city}`,
): { q: string; a: string } => {
  const routes = placeId ? routesFor(placeId, locale) : [];
  if (locale === "en") {
    const fixed = routes.length
      ? `Journeys from ${city} are fixed prices: ${routes.map((r) => `${r.other} from €${r.min}`).join(", ")}.`
      : `A journey from ${city} without a published fixed price is estimated per kilometre, from €${km("en")} per km in a Class E, and the price is confirmed in writing before the ride.`;
    return {
      q: `How much does a private chauffeur cost in ${city}?`,
      a: `${fixed} To keep the chauffeur, hourly hire starts at €${P.e3} for 3 hours in a Class E (€${P.v3} in a 7-seat Class V, €${P.s3} in a Class S), and an 8-hour day costs €${P.e8} to €${P.s8}. These are our public rates, and the final price is confirmed in writing before the service.`,
    };
  }
  if (locale === "ar") {
    const fixed = routes.length
      ? `الرحلات من ${city} بسعر ثابت: ${routes.map((r) => `${r.other} ابتداءً من ${r.min} يورو`).join("، ")}.`
      : `تُقدَّر الرحلة من ${city} من دون سعر ثابت معلن بالكيلومتر، ابتداءً من ${km("ar")} يورو للكيلومتر في الفئة E، ويُؤكَّد السعر كتابيًا قبل الرحلة.`;
    return {
      q: `كم تكلفة سائق خاص في ${city}؟`,
      a: `${fixed} ولإبقاء السائق، تبدأ الخدمة بالساعة من ${P.e3} يورو لثلاث ساعات في الفئة E (${P.v3} يورو في الفئة V ذات 7 مقاعد، و${P.s3} يورو في الفئة S)، ويكلّف يوم من 8 ساعات بين ${P.e8} و${P.s8} يورو. هذه أسعارنا المعلنة، ويُؤكَّد السعر النهائي كتابيًا قبل الخدمة.`,
    };
  }
  const fixed = routes.length
    ? `Les trajets depuis ${city} sont à prix fixe : ${routes.map((r) => `${r.other} dès ${r.min} €`).join(", ")}.`
    : `Un trajet depuis ${city} sans forfait publié est estimé au kilomètre, à partir de ${km("fr")} € du kilomètre en Classe E, et le prix est confirmé par écrit avant la course.`;
  return {
    q: `Quel est le prix d'un chauffeur privé ${at} ?`,
    a: `${fixed} Pour garder le chauffeur, la mise à disposition démarre à ${P.e3} € les 3 h en Classe E (${P.v3} € en Classe V 7 places, ${P.s3} € en Classe S), et une journée de 8 h coûte de ${P.e8} à ${P.s8} €. Ce sont nos tarifs publics, et le prix final est confirmé par écrit avant la prestation.`,
  };
};
